import { useState } from "react";
import { MessageCircle, Eye } from "lucide-react";
import { TIER_LABEL, SIZES, DEFAULT_SIZE } from "@/data/products";
import { brl } from "@/lib/format";
import { PRODUCT } from "@/constants/testIds";
import { waLink, buildQuickProductMessage } from "@/lib/whatsapp";
import { trackWhatsAppClick } from "@/lib/analytics";

export const ProductCard = ({ product, onClick }) => {
  const [sizeId, setSizeId] = useState(DEFAULT_SIZE);
  const size = SIZES.find((s) => s.id === sizeId);
  const price = product.sizes[sizeId];
  const quickBuyHref = waLink(buildQuickProductMessage({ product, size, price }));

  return (
    <article
      data-testid={PRODUCT.card(product.ref)}
      className="group bg-white border border-[#E5E3DB] rounded-2xl overflow-hidden flex flex-col transition-all duration-500 hover:-translate-y-1 hover:border-[#2D4A3E] hover:shadow-[0_30px_60px_-30px_rgba(45,74,62,0.25)]"
    >
      <button
        onClick={onClick}
        data-testid={PRODUCT.openBtn(product.ref)}
        className="text-left"
        aria-label={`Ver detalhes ${product.name}`}
      >
        <div className="relative aspect-[4/5] overflow-hidden bg-[#F4F2EC]">
          <img
            src={product.img}
            alt={`Coroa de flores ${product.ref} — ${product.description} · Entrega grátis Rio de Janeiro`}
            loading="lazy"
            decoding="async"
            width="800"
            height="1000"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <span className="absolute top-3 left-3 bg-white/90 backdrop-blur text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full text-[#2D4A3E] border border-white">
            {TIER_LABEL[product.tier]}
          </span>
        </div>
      </button>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-xl font-semibold text-[#2D4A3E] leading-tight">{product.name}</h3>
          <span className="text-[10px] tracking-[0.2em] uppercase text-[#BFA573] mt-1.5">{product.ref}</span>
        </div>
        <p className="text-sm text-[#5C5C5C] mt-2 leading-relaxed line-clamp-2">{product.description}</p>

        <div className="mt-4">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#BFA573] mb-2">Tamanho</p>
          <div className="grid grid-cols-4 gap-1.5">
            {SIZES.map((s) => {
              const active = s.id === sizeId;
              return (
                <button
                  key={s.id}
                  onClick={(e) => { e.stopPropagation(); setSizeId(s.id); }}
                  data-testid={`size-${product.ref}-${s.id}`}
                  aria-pressed={active}
                  className={`py-2 rounded-lg text-[11px] leading-tight font-medium border transition-all ${
                    active
                      ? "bg-[#2D4A3E] text-white border-[#2D4A3E]"
                      : "bg-white text-[#1C1C1C] border-[#E5E3DB] hover:border-[#2D4A3E]"
                  }`}
                >
                  <div>{s.label}</div>
                  <div className={`text-[9px] mt-0.5 ${active ? "text-white/80" : "text-[#5C5C5C]"}`}>{s.dim}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-[#E5E3DB]">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#BFA573]">Valor · {size.label} {size.dim}</p>
          <p className="font-semibold text-[26px] leading-none text-[#1C1C1C] mt-1 tabular-nums tracking-tight">
            {brl(price)}
          </p>
        </div>

        <div className="mt-4 grid grid-cols-[1fr_auto] gap-2 mt-auto pt-1">
          <a
            href={quickBuyHref}
            target="_blank"
            rel="noreferrer"
            data-testid={`product-buy-whatsapp-${product.ref}`}
            onClick={() => trackWhatsAppClick("product_card", { ref: product.ref, price: product.price })}
            className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FB855] text-white font-medium px-4 py-2.5 rounded-full text-sm transition-all duration-300 hover:-translate-y-0.5 shadow-[0_8px_20px_-8px_rgba(37,211,102,0.6)]"
          >
            <MessageCircle className="h-4 w-4" strokeWidth={2} />
            Comprar
          </a>
          <button
            onClick={onClick}
            data-testid={`product-details-${product.ref}`}
            aria-label="Ver detalhes e personalizar"
            className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-[#E5E3DB] text-[#2D4A3E] hover:border-[#2D4A3E] transition-colors"
          >
            <Eye className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </article>
  );
};
